import debounce from 'awesome-debounce-promise';
import React from 'react';

import type { DropdownFieldProps } from 'src/shared/components/forms/DropdownField';
import { DropdownField } from 'src/shared/components/forms/DropdownField';
import type { AffiliatesQuery } from 'src/shared/graphql/generated';
import {
  PartnersWithAffiliateOrgsIdentifiers,
  useAffiliatesLazyQuery,
} from 'src/shared/graphql/generated';
import { useConstant } from 'src/shared/hooks';

type Affiliate = AffiliatesQuery['affiliateOrgsForPartner']['results'][number];

interface ValidAffiliate extends NonNullable<Affiliate> {
  customerDisplayName: string;
  orgCode: {
    code: string;
  };
}

type AffiliateDropdownProps = DropdownFieldProps<ValidAffiliate>;

export interface BranchDropdownFieldProps
  extends Omit<
    AffiliateDropdownProps,
    | 'itemToKey'
    | 'itemToString'
    | 'itemToValue'
    | 'items'
    | 'onInputValueChange'
  > {}

const QUERY_DEBOUNCE_WAIT_MILLISECONDS = 250;

/**
 * Type guard for filtering out potentially invalid `OrgBasicInfo` objects. I'm
 * pretty sure they won't exist at runtime, but our schema defines these fields
 * we need to display and persist things as optional.
 *
 * @param affiliate
 * @returns
 */
const isValidAffiliate = (affiliate: Affiliate): affiliate is ValidAffiliate =>
  !!affiliate &&
  typeof affiliate.orgCode?.code === 'string' &&
  typeof affiliate.customerDisplayName === 'string';

const BranchDropdownField = (props: BranchDropdownFieldProps) => {
  const [queryAffiliates, { loading, data, previousData }] =
    useAffiliatesLazyQuery();

  const search = useConstant(() =>
    debounce(queryAffiliates, QUERY_DEBOUNCE_WAIT_MILLISECONDS),
  );

  const affiliates = (
    data ?? previousData
  )?.affiliateOrgsForPartner.results.filter(isValidAffiliate);

  return (
    <DropdownField
      isLoading={loading}
      itemToKey={({ id }) => id}
      itemToString={item => item?.customerDisplayName ?? ''}
      itemToValue={item => item.orgCode.code}
      items={affiliates ?? ([] as ValidAffiliate[])}
      onInputValueChange={({ inputValue }) =>
        inputValue &&
        inputValue.length >= 3 &&
        search({
          variables: {
            partnerIdentifier: PartnersWithAffiliateOrgsIdentifiers.Beacon,
            search: inputValue,
          },
        })
      }
      {...props}
    />
  );
};

export { BranchDropdownField };
